import React, { useState, useEffect } from "react";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from '../Context/AuthContext'; 
import * as XLSX from "xlsx";


const GSTDashboard = () => {

    const { user } = useAuth();

    const [filters, setFilters] = useState({
        organisationId: "",
        fiscalYear: new Date().getFullYear(),
        fiscalMonth: "",
        source: "",
        partner: ""
      });

     const [sources, setSources] = useState([]);

    const [openSection, setOpenSection] = useState({
    payable: false,  
    monthwise: false,
    partner: false,
    invoice: false,
    item: false
    });
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  // 🔹 Handle input
  const handleChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value.trim()
    });
  };

  const currentYear = new Date().getFullYear();

  // 🔹 Fetch data for a section
  const fetchData = async (type) => {
    try {
      setLoading(true);
  
      let url = "";
  
      switch (type) {
        case "payable":   
        url = `${Base_URL}/GSTDashboard/payable`;
        break;
    
        case "monthwise":
          url = `${Base_URL}/GSTDashboard/month`;
          break;
        case "partner":
          url = `${Base_URL}/GSTDashboard/header`;
          break;
        case "invoice":
          url = `${Base_URL}/GSTDashboard/summary`;
          break;
        case "item":
          url = `${Base_URL}/GSTDashboard/detail`;
          break;
        default:
          return;
      }

      const normalizeKeys = (rows) => {
        return rows.map((row) => {
          const newRow = {};
      
          Object.keys(row).forEach((key) => {
            const lowerKey = key.toLowerCase();
      
            // ✅ Normalize Partner fields
            if (lowerKey === "customerid" || lowerKey === "partnerid") {
              newRow["partnerId"] = row[key];
            }
            else if (lowerKey === "customer" || lowerKey === "partnername" || lowerKey === "partner") {
              newRow["partner"] = row[key];
            }
            else {
              newRow[key] = row[key];
            }
          });
      
          return newRow;
        });
      };
  
      const params = {
        orgId: filters.organisationId,
        fiscalYear: filters.fiscalYear === "ALL" ? null : filters.fiscalYear,
        fiscalMonth: filters.fiscalMonth || null
      };
      
      if (type !== "payable") {
        params.source = filters.source || null;
      }
      
      const res = await axios.get(url, { params });
      const normalizedData = normalizeKeys(res.data || []);

          
  
      setData((prev) => ({
        ...prev,
        [type]: normalizedData
      }));
  
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Toggle section
  const toggleSection = (type) => {
    setOpenSection((prev) => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const formatHeader = (key) => {
    if (!key) return "";

    const customMap = {
      partnerId: "Partner ID",
        customerId: "Partner ID",
        partnerId: "Partner ID",
        customer: "Partner",
        partnerName: "Partner"
      };
    
      if (customMap[key]) return customMap[key];
  
    let result = key
      .replace(/([a-z])([A-Z])/g, "$1 $2") // camelCase → space
      .replace(/_/g, " "); // snake_case → space
  
    const upperWords = ["cgst", "sgst", "igst", "gst", "id"];
  
    result = result
      .split(" ")
      .map((word) => {
        if (upperWords.includes(word.toLowerCase())) {
          return word.toUpperCase(); // CGST, ID
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(" ");
  
    return result;
  };
  

  // 🔹 Download Excel
  const handleDownload = () => {
    try {
      const workbook = XLSX.utils.book_new();
  
      // 🔹 Helper function to add sheet
      const addSheet = (data, sheetName) => {
        if (!data || data.length === 0) {
          const ws = XLSX.utils.aoa_to_sheet([["No Data Available"]]);
          XLSX.utils.book_append_sheet(workbook, ws, sheetName);
          return;
        }
  
        const ws = XLSX.utils.json_to_sheet(data);

// 🔹 Format headers
        const headers = Object.keys(data[0]);

        headers.forEach((key, index) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });

        if (ws[cellRef]) {
            ws[cellRef].v = formatHeader(key); // ✅ formatted header
        }
        });

        headers.forEach((key, index) => {
            const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
          
            if (ws[cellRef]) {
              ws[cellRef].v = formatHeader(key);
              ws[cellRef].s = {
                font: { bold: true },
                alignment: { horizontal: "center" }
              };
            }
          });
  
        // 🔥 Auto column width
        const colWidths = Object.keys(data[0]).map((key) => ({
          wch: Math.max(
            key.length,
            ...data.map((row) => (row[key] ? row[key].toString().length : 10))
          )
        }));
  
        ws["!cols"] = colWidths;
  
        XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      };
  
      // 🔹 Add all 4 sections
      addSheet(data.payable, "Payable"); 
      addSheet(data.monthwise, "Monthwise");
      addSheet(data.partner, "PartnerWise");
      addSheet(data.invoice, "InvoiceWise");
      addSheet(data.item, "ItemWise");
  
      // 🔹 Download file
      XLSX.writeFile(workbook, "GST_Report.xlsx");
  
    } catch (err) {
      console.error("Excel Download Error:", err);
    }
  };

  // 🔹 Columns
  const getColumns = (rows) => {
    if (!rows || rows.length === 0) return [];
    return Object.keys(rows[0]);
  };

  const sections = [
    { key: "payable", title: "Payable Summary" }, 
    { key: "monthwise", title: "Monthwise Summary" },
    { key: "partner", title: "Partner-wise Summary" },
    { key: "invoice", title: "Invoice-wise Summary" },
    { key: "item", title: "Item-wise Summary" }
  ];

  const monthsList = [
    { label: "January", value: "January" },
    { label: "February", value: "February" },
    { label: "March", value: "March" },
    { label: "April", value: "April" },
    { label: "May", value: "May" },
    { label: "June", value: "June" },
    { label: "July", value: "July" },
    { label: "August", value: "August" },
    { label: "September", value: "September" },
    { label: "October", value: "October" },
    { label: "November", value: "November" },
    { label: "December", value: "December" }
  ];

  const extractSources = (allData) => {
    const sourceSet = new Set();
  
    Object.values(allData).forEach((rows) => {
      rows.forEach((x) => {
        if (x.Source || x.source) {
          sourceSet.add(x.Source || x.source);
        }
      });
    });
  
    setSources(Array.from(sourceSet));  
  };

  useEffect(() => {
    extractSources(data);
  }, [data]);

  useEffect(() => {
    if (!filters.organisationId) return;
    fetchData("payable"); 
    fetchData("monthwise");
    fetchData("partner");
    fetchData("invoice");
    fetchData("item");
  
  }, [filters.organisationId, filters.fiscalYear, filters.fiscalMonth, filters.source]);
  useEffect(() => {
    if (user?.organisationId) {
      setFilters((prev) => ({
        ...prev,
        organisationId: user.organisationId,
        fiscalYear: new Date().getFullYear() // ✅ ensure always current
      }));
    }
  }, [user]);

  return (
    <div className="container mt-3">

      {/* 🔹 Header */}
      <div className="d-flex justify-content-between align-items-center">
        <h3>GST Dashboard</h3>
        <button className="btn btn-success" onClick={handleDownload}>
          Export to Excel
        </button>
      </div>

      {/* 🔹 Filters */}
      <div className="card p-3 mt-3">
        <div className="row">

          <div className="col">
          <input
            name="organisationId"
            className="form-control"
            value={filters.organisationId}
            readOnly
            />
          </div>

          <div className="col">
          <select
  name="fiscalYear"
  className="form-control"
  value={filters.fiscalYear}
  onChange={handleChange}
>
  <option value="ALL">All Years</option>
  <option value="2026">2026</option>
  <option value="2025">2025</option>
  <option value="2024">2024</option>
</select>
          </div>

          <div className="col">
          <select
  name="fiscalMonth"
  className="form-control"
  value={filters.fiscalMonth}
  onChange={handleChange}
>
  <option value="">All Months</option>

  {monthsList.map((m, i) => (
    <option key={i} value={m.value}>
      {m.label}
    </option>
  ))}
</select>
          </div>

          <div className="col">
          <select
  name="source"
  className="form-control"
  value={filters.source}
  onChange={handleChange}
>
  <option value="">All Sources</option>

  {sources.map((s, i) => (
    <option key={i} value={s}>
      {s}
    </option>
  ))}
</select>
          </div>

          {/* <div className="col">
            <input name="partner" placeholder="Partner"
              className="form-control" onChange={handleChange} />
          </div> */}

        </div>
      </div>

      {/* 🔹 Accordion Sections */}
      <div className="mt-4">

        {sections.map((section) => {
          const isOpen = openSection[section.key];
          const rows = data[section.key] || [];
          const columns = getColumns(rows);

          return (
            <div key={section.key} className="mb-3">

              {/* 🔹 Grey Header */}
              <div
                onClick={() => toggleSection(section.key)}
                style={{
                  background: "#e5e7eb",
                  padding: "15px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "500"
                }}
              >
                {section.title}
                <span>{isOpen ? "▲" : "▼"}</span>
              </div>

              {/* 🔹 Expand Content */}
              {isOpen && (
                <div className="mt-2 p-2 border">

                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          {columns.map((col, i) => (
                            <th key={i} >
                            {formatHeader(col)}
                          </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {rows.length > 0 ? (
                          rows.map((row, i) => (
                            <tr key={i}>
                              {columns.map((col, j) => (
                                <td key={j}>{row[col] ?? "-"}</td>
                              ))}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={columns.length} className="text-center">
                              No Data Available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}

                </div>
              )}

            </div>
          );
        })}

      </div>

    </div>
  );
};

export default GSTDashboard;